import Account from "../../Account";
import type GameSet from "../../GameSet";
import { isPresent } from "@mixitone/util";
import type { FillContext, FillMode } from "./types";

export function buildFillContext(gameSet: GameSet, opts?: { seed?: number }): FillContext {
  const lockedPlayerIds = new Set(
    gameSet.games.filter((game) => game.locked).flatMap((game) => game.activePlayerIds()),
  );
  const seatedIds = new Set(gameSet.playerIds());
  const players = gameSet.night
    .uniqueActivePlayers(seatedIds)
    .filter((player) => player.id && !lockedPlayerIds.has(player.id));
  const playerIds = players.map((player) => player.id!).filter(isPresent);
  const activePlayerIds = new Set(playerIds);

  const playerGroups: Record<string, string> = {};
  for (const player of players) {
    const id = player.id;
    const groupId = player.group?.id;
    if (!id || !groupId) continue;
    playerGroups[id] = groupId;
  }

  const playerPairs: Record<string, string> = {};
  for (const player of players) {
    const id = player.id;
    const partnerId = player.partner_id;
    if (!id || !partnerId || !activePlayerIds.has(partnerId)) continue;
    playerPairs[id] = partnerId;
    playerPairs[partnerId] = id;
  }

  const groupLevelsEnabled = Account.current?.hasFeature("group_levels") ?? false;
  const playerLevels: Record<string, number | undefined> = {};
  if (groupLevelsEnabled) {
    for (const player of players) {
      const id = player.id;
      if (!id) continue;
      if (!player.group?.graduation_enabled) continue;
      if (!player.clubPlayer.loaded) continue;
      const level = player.clubPlayer.graduation_level;
      playerLevels[id] = isPresent(level) ? level : undefined;
    }
  }

  const setIndex = gameSet.index!;
  const timesOut: Record<string, number> = {};
  for (const player of players) {
    const id = player.id!;
    let count = 0;
    for (const [idx, set] of gameSet.night.game_sets.entries()) {
      if (idx >= (player.joined_game_set_index ?? 0) && idx < setIndex && !set.hasPlayer(id)) {
        count++;
      }
    }
    timesOut[id] = count;
  }

  let satOutLastSet: string[] = [];
  if (setIndex > 0) {
    const previousSetPlayerIds = new Set(gameSet.night.game_sets[setIndex - 1]?.playerIds() ?? []);
    satOutLastSet = players
      .filter((player) => !previousSetPlayerIds.has(player.id!))
      .map((player) => player.id!);
  }

  const previousSets = gameSet.night.game_sets
    .filter((set) => set.index! < setIndex)
    .map((set) => ({
      setIndex: set.index!,
      games: set.games.map((game) => game.activePlayerIds()),
    }));

  const games = gameSet.unlockedGames.map((game) => ({
    gameIndex: game.index!,
    fixed: [...game.playerIds],
  }));

  return {
    mode: (gameSet.night.auto_fill_mode || "doubles_only") as FillMode,
    seed: opts?.seed ?? Date.now(),
    playerIds,
    playerGroups,
    groupExclusions: gameSet.club.groupExclusions,
    playerPairs,
    playerLevels,
    timesOut,
    satOutLastSet,
    previousSets,
    currentSetIndex: setIndex,
    games,
  };
}
